import { useState } from "react";
import { motion } from "framer-motion";
import { http, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [sent, setSent] = useState(false);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await http.post("/contact", form);
            setSent(true);
            setForm({ name: "", email: "", company: "", message: "" });
            toast.success("Mensaje enviado. Te escribimos pronto.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "No se ha podido enviar.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="px-6 md:px-12 py-24 md:py-48 border-t border-white/5">
            <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24 items-end">
                <p className="col-span-12 md:col-span-2 text-[11px] tracking-[0.32em] uppercase text-white/45">
                    04 — Contact
                </p>
                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="col-span-12 md:col-span-10 font-display tracking-tightest leading-[0.82] text-5xl md:text-7xl lg:text-[120px]"
                >
                    Let's make
                    <br />
                    something
                    <br />
                    <span className="text-[color:var(--midi-accent-strong)]">memorable.</span>
                </motion.h2>
            </div>

            <div className="grid grid-cols-12 gap-6 md:gap-10">
                <div className="col-span-12 md:col-span-4 space-y-6 text-white/70">
                    <div>
                        <p className="text-[10px] tracking-[0.32em] uppercase text-white/40 mb-2">Email</p>
                        <a
                            data-testid="contact-email-link"
                            href="mailto:hola@midipictures.com"
                            className="midi-link text-white text-lg md:text-xl"
                        >
                            hola@midipictures.com
                        </a>
                    </div>
                    <div>
                        <p className="text-[10px] tracking-[0.32em] uppercase text-white/40 mb-2">Studio</p>
                        <p className="text-white/85 text-lg">Madrid · Worldwide</p>
                        <p className="text-white/60 text-sm font-light mt-1">C/ Aguacate 41, Portal 4, Local 9</p>
                    </div>
                    <div>
                        <p className="text-[10px] tracking-[0.32em] uppercase text-white/40 mb-2">Press / Briefs</p>
                        <p className="text-white/60 max-w-xs text-sm font-light leading-relaxed">
                            Cuéntanos tu proyecto, plazos y referencias. Respondemos en menos de 48 horas.
                        </p>
                    </div>
                </div>

                <form
                    data-testid="contact-form"
                    onSubmit={onSubmit}
                    className="col-span-12 md:col-span-8 md:col-start-5 space-y-10"
                >
                    {[
                        { name: "name", label: "Tu nombre", type: "text", required: true },
                        { name: "email", label: "Email", type: "email", required: true },
                        { name: "company", label: "Marca / proyecto", type: "text", required: false },
                    ].map((f) => (
                        <Field key={f.name} {...f} value={form[f.name]} onChange={onChange} />
                    ))}

                    <div className="relative">
                        <label className="text-[10px] tracking-[0.32em] uppercase text-white/40 block mb-3">
                            Mensaje
                        </label>
                        <textarea
                            data-testid="contact-input-message"
                            name="message"
                            required
                            value={form.message}
                            onChange={onChange}
                            rows={4}
                            placeholder="Cuéntanos qué quieres contar..."
                            className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--midi-accent-strong)] outline-none py-3 text-lg md:text-xl text-white placeholder:text-white/30 font-light transition-colors duration-500 resize-none"
                        />
                    </div>

                    <button
                        data-testid="contact-submit"
                        type="submit"
                        disabled={submitting}
                        className="group inline-flex items-center gap-4 text-white border-b border-white/30 pb-3 pr-1 hover:border-[color:var(--midi-accent-strong)] transition-colors duration-700 disabled:opacity-50"
                    >
                        <span className="font-display tracking-tightest text-2xl md:text-3xl">
                            {submitting ? "Enviando..." : sent ? "Enviado" : "Enviar mensaje"}
                        </span>
                        <span className="block w-10 h-px bg-white group-hover:w-16 group-hover:bg-[color:var(--midi-accent-strong)] transition-all duration-700" />
                    </button>
                </form>
            </div>
        </section>
    );
}

function Field({ name, label, type, required, value, onChange }) {
    return (
        <div>
            <label className="text-[10px] tracking-[0.32em] uppercase text-white/40 block mb-3">{label}</label>
            <input
                data-testid={`contact-input-${name}`}
                name={name}
                type={type}
                required={required}
                value={value}
                onChange={onChange}
                className="w-full bg-transparent border-b border-white/15 focus:border-[color:var(--midi-accent-strong)] outline-none py-3 text-lg md:text-xl text-white placeholder:text-white/30 font-light transition-colors duration-500"
            />
        </div>
    );
}
